import { debounce } from 'lodash';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import React, { useCallback, useState, useEffect, useContext, useRef } from 'react';
import { Avatar, Row, Col, Spin, Card, notification, Button, Modal, Tooltip, Typography} from 'antd';
import { UserContext } from '../UserContext';
import axiosInstance from '../axiosInstance';
import API_ENDPOINTS from '../../apiConfig';
import TextArea from 'antd/es/input/TextArea';
import { v4 as uuidv4 } from 'uuid';  

// Components to be loaded as modal
import CommentParser from './CommentParser';
import Reports from '../../Pages/Reports';

const {Paragraph} = Typography;

const DemoAgent = () => {
    const initialMessage = "Hello, how can I help you?";
    const [inputValue, setInputValue] = useState('');
    const [question, setQuestion] = useState('');
    const { uid, token, setCurrentStep, setSelectedReportKey, setSelectedReport} = useContext(UserContext);
    const [resultLoading, setResultLoading] = useState(false);
    const [conversation, setConversation] = useState([
        {
            type: "ai",
            elements: [
                { text: initialMessage },
            ]
        }
    ]);
    const messagesEndRef = useRef(null);
    const [selectedDataFile, setSelectedDataFile] = useState(null); // Ensure it only accepts specific strings
    const [threadId, setThreadId] = useState(uuidv4());  // Initial thread_id

    // Single state for modal visibility and modal content type
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalContentType, setModalContentType] = useState(''); // e.g., 'comment_parser' or 'report_module'

    const sendQuestion = async (query) => {
        if (!query) return; 
        const trimmedQuestion = query.trim();  
    
        if (!trimmedQuestion) return;  // Prevent empty messages
    
        setInputValue('');  
        setQuestion(trimmedQuestion); 
    
        const parserMapping = {
            'start_comment_parser_actuals': 'actuals',
            'start_comment_parser_forecast': 'forecast',
            'start_comment_parser_budget': 'budget',
        };
    
        if (parserMapping[trimmedQuestion]) {
            setSelectedDataFile(parserMapping[trimmedQuestion]);
            setModalContentType('comment_parser');
            setIsModalOpen(true);
            return;
        }
    
        if (trimmedQuestion === 'start_report_module') {
            setModalContentType('report_module');
            setCurrentStep(1);
            setSelectedReportKey("Market Analysis LB Report_TIGT");
            setSelectedReport("Market Analysis LB Report");
            setIsModalOpen(true);
            return;
        }
    
        setResultLoading(true);
        setConversation(prev => [...prev, { type: 'human', elements: [{ text: trimmedQuestion }] }]);
    
        try {
            const url = `${API_ENDPOINTS.demo}/demo_agent?user_id=${uid}&thread_id=${threadId}`;
            const response = await axiosInstance(token).post(url, { query: trimmedQuestion });
    
            console.log("API Response Elements:", JSON.stringify(response.data.response.elements, null, 2));
    
            setConversation(prev => [...prev, { type: 'ai', elements: response.data.response.elements }]);
    
        } catch (error) {
            console.error(error);
            notification.error({
                message: 'Error',
                description: 'Failed to send question. Please try again later.',
            });
        } finally {
            setResultLoading(false);
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const handleOptionClick = (value) => {
        console.log("Button Clicked:", value);  // ✅ Debug log
    
        if (typeof value === 'string' && value.startsWith('http')) {
            window.open(value, '_blank');
        } else {
            sendQuestion(value);  // 
        }
    };

    useEffect(() => {
        requestAnimationFrame(scrollToBottom);
    }, [conversation]);

    const clearChat = () => {
        setConversation([{ type: "ai", elements: [{ text: initialMessage }] }]);
        setThreadId(uuidv4());  // Generate a new UUID when clearing chat
    };

    const handleCancel = () => {
        setIsModalOpen(false);  // Close the modal
        setModalContentType('');  // Reset the modal content type
    };

    const updateQuestion = useCallback(
        debounce((value) => {
          setQuestion(value);  // Updates only after user stops typing for 300ms
        }, 300),
        []
      );
      
      const handleChange = (e) => {
        setInputValue(e.target.value);  // Update the UI immediately
    };

    const getMarkdownToRender = (element) => {
        return element.text.trim().startsWith('|')
          ? '\n' + element.text.trim()
          : element.text.trim();
      };
      
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'left', height: '80vh', width: '100%' }}>
            <div style={{
                overflowY: 'auto',
                overflowX: 'hidden',
                flexGrow: 1,
            }}>
                {conversation.map((entry, index) => (
                    <div key={index} style={{
                        display: 'flex', 
                        width: '100%', 
                        justifyContent: entry.type === 'human' ? 'flex-end' : 'flex-start', 
                        marginBottom: '10px',
                    }}>
                        <Card
                            size="small"
                            style={{
                                // whiteSpace: 'pre-wrap',
                                backgroundColor: entry.type === 'human' ? '#f5f5f5' : '#fff',
                                border: entry.type === 'human' ? '2px solid #1890ff' : '#ffffff',
                                borderRadius: '10px',
                                maxWidth: '80%',
                            }}
                        >
                            {entry.elements.map((element, elIndex) => (
                                element.text ? (
                                    <Paragraph key={elIndex} style={{ marginBottom: '2px' }}>
                                        <ReactMarkdown
                                            key={elIndex}    
                                            skipHtml
                                            remarkPlugins={[remarkGfm]}
                                            rehypePlugins={[rehypeSanitize]}
                                            components={{
                                                table: ({ node, ...props }) => (
                                                    <table {...props} style={{ borderCollapse: 'collapse', width: '100%', marginBottom: '16px' }} />
                                                ),
                                                thead: ({ node, ...props }) => <thead {...props} />,
                                                tbody: ({ node, ...props }) => <tbody {...props} />,
                                                tr: ({ node, ...props }) => <tr {...props} style={{ borderBottom: '1px solid #ddd' }} />,
                                                th: ({ node, ...props }) => (
                                                    <th {...props} style={{ backgroundColor: '#f4f4f4', padding: '8px', border: '1px solid #ddd' }} />
                                                ),
                                                td: ({ node, ...props }) => (
                                                    <td {...props} style={{ padding: '8px', border: '1px solid #ddd' }} />
                                                ),
                                                p: ({ node, ...props }) => <p {...props} style={{ marginBottom: '8px' }} />,
                                                li: ({ node, ordered, ...props }) => (
                                                    <li {...props} style={{ marginBottom: '2px', listStyleType: ordered ? 'decimal' : 'disc' }} />
                                                ),
                                                ul: ({ node, ...props }) => <ul {...props} style={{ paddingLeft: '16px', marginBottom: '2px' }} />,
                                                ol: ({ node, ...props }) => <ol {...props} style={{ paddingLeft: '16px', marginBottom: '2px' }} />,
                                                h1: ({ node, ...props }) => <h1 style={{ marginBottom: '6px', fontSize: '1.5em' }} {...props} />,
                                                h2: ({ node, ...props }) => <h2 style={{ marginBottom: '6px', fontSize: '1.3em' }} {...props} />,
                                                h3: ({ node, ...props }) => <h3 style={{ marginBottom: '6px', fontSize: '1.2em' }} {...props} />,
                                            }}
                                        >
                                        
                                            {getMarkdownToRender(element)}
                                        </ReactMarkdown>
                                    </Paragraph>
                                  
                                ) : (
                                    <Tooltip key={elIndex} title={element.value}>
                                        <Button
                                            onClick={() => handleOptionClick(element.value)}
                                            style={{
                                                display: 'block',
                                                margin: '5px 0',
                                            }}
                                        >
                                            {element.label}
                                        </Button>
                                    </Tooltip>
                                )
                            ))}
                        </Card>
                    </div>
                ))}

                <div ref={messagesEndRef} />
            </div>
            <div style={{ padding: '10px 20px', backgroundColor: '#fff', width: '100%' }}>
                <Row justify="center" style={{ width: '100%' }}>
                    <Col span={24} style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
                    <TextArea
                        rows={3}
                        value={inputValue}
                        onChange={handleChange}
                        onPressEnter={(e) => {
                            e.preventDefault();
                            sendQuestion(inputValue);
                        }}
                        placeholder="Type your message here..."
                        disabled={resultLoading}
                        style={{
                            width: '100%',
                            margin: '0 auto',
                            minHeight: '50px',
                        }}
                    />
                        {resultLoading && (
                            <Spin style={{
                                position: 'absolute',
                                zIndex: 2,
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'rgba(255, 255, 255, 0.8)' // Semi-transparent background
                            }} />
                        )}
                    </Col>
                </Row>
                <Row justify="center" style={{ width: '100%', marginTop: '10px' }}>
                    <Col span={24} style={{ display: 'flex', justifyContent: 'center' }}>
                        <Button danger onClick={clearChat} style={{ marginRight: '8px' }} disabled={resultLoading}>
                            New Thread
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => sendQuestion(inputValue)}
                            loading={resultLoading}
                            disabled={resultLoading || !inputValue.trim()}>
                            Send
                        </Button>
                    </Col>
                </Row>

                {/* Single Modal */}
                <Modal
                    title={modalContentType === 'comment_parser' ? "Comment Parser" : "Report Module"}
                    open={isModalOpen}
                    onCancel={handleCancel}
                    footer={null}
                    width="90%"
                >
                    {/* Conditional rendering of modal content based on the modalContentType */}
                    {modalContentType === 'comment_parser' && (
                        <CommentParser
                            selectedCollectionKey="ed69934d-3f58-4b95-ac3d-245a207d813c"
                            selectedCollection="Comments Dataset"
                            selectedDataFile={selectedDataFile}
                        />
                    )}
                    {modalContentType === 'report_module' && (
                        <Reports 
                            


                        />
                    )}
                </Modal>
            </div>
        </div>
    );
}

export default DemoAgent;